// Service sécurisé pour appeler Gemini via Edge Function
// Remplace les appels directs à Gemini API côté client

import { analyticsErrorCode } from "@core/lib/analyticsErrors";
import { logger } from "../lib/logger";
import { handleError, logError } from "../lib/error-handling";
import { getEnv } from "../lib/env";
import {
  getSupabaseSessionWithTimeout,
  invokeSupabaseFunction,
} from "../lib/supabaseApi";
import { getTurnstileToken } from "../lib/turnstileToken";
import { getCachedFingerprint } from "../lib/browserFingerprint";
import type { GeminiAttachedFile } from "./FileAttachmentService";

export interface SecureGeminiResponse {
  success: boolean;
  data?: string;
  error?: string;
  message?: string;
  creditsRemaining?: number;
  timings?: {
    total: number;
    gemini: number;
    quota: number;
    init: number;
    auth: number;
    rateLimit: number;
  };
}

/** Identifiant opaque d'une conversation de chat (8-64 car. [A-Za-z0-9_-]). */
export function generateConversationScope(): string {
  const c = typeof crypto !== "undefined" ? crypto : undefined;
  if (c?.randomUUID) return c.randomUUID();
  return `c${Date.now().toString(36)}${Math.random().toString(36).slice(2, 12)}`;
}

export class SecureGeminiService {
  private static instance: SecureGeminiService;
  private supabaseUrl: string;
  private readonly MAX_RETRIES = 3;
  private readonly RETRY_DELAY_MS = 1000;
  private cachedSession: { token: string; expiresAt: number } | null = null;
  // Identifiant opaque de la conversation de chat en cours (#1412), envoyé à
  // hyper-task pour le plafond serveur de messages par conversation.
  private conversationScope: string | null = null;

  public setConversationScope(scope: string | null): void {
    this.conversationScope = scope;
  }

  public static getInstance(): SecureGeminiService {
    if (!SecureGeminiService.instance) {
      SecureGeminiService.instance = new SecureGeminiService();
    }
    return SecureGeminiService.instance;
  }

  private constructor() {
    const envSupabaseUrl = getEnv("VITE_SUPABASE_URL");

    if (envSupabaseUrl) {
      this.supabaseUrl = envSupabaseUrl;
    } else {
      // FORCER l'URL Supabase par défaut pour éviter les problèmes sur Vercel
      this.supabaseUrl = "https://outmbbisrrdiumlweira.supabase.co";
    }

    // DEBUG: Log complet pour diagnostic
    // DEBUG: Log complet pour diagnostic (activé uniquement en dev)
    const isDev = typeof import.meta !== "undefined" && import.meta.env?.DEV;
    if (isDev) {
      console.log("[SecureGeminiService] Initialized", {
        url: this.supabaseUrl,
      });
    }
  }

  /**
   * Détermine si une erreur justifie un retry. Les erreurs métier (4xx)
   * doivent échouer rapidement — retry sur QUOTA/UNAUTHORIZED/TURNSTILE
   * gaspille du temps (jusqu'à 7s de delay cumulé) sans changer le résultat.
   * Retry uniquement sur erreurs réseau, timeout et 5xx.
   */
  private isRetryable(error: unknown): boolean {
    const message = error instanceof Error ? error.message : String(error);
    const lower = message.toLowerCase();

    // Erreurs métier non-retryables — l'utilisateur doit voir l'erreur tout de suite.
    const nonRetryablePatterns = [
      "quota_exceeded",
      "analytics_", // refus du quota analytics (#1475)
      "quota",
      "unauthorized",
      "401",
      "403",
      "turnstile",
      "rate_limit",
      "rate limit",
      "validation",
      "invalid",
      "forbidden",
    ];
    if (nonRetryablePatterns.some((p) => lower.includes(p))) return false;

    // Retry sur réseau/timeout/5xx
    return true;
  }

  /**
   * Retry helper avec backoff exponentiel.
   * Court-circuite sur erreurs métier non-retryables (cf. isRetryable).
   */
  private async retryWithBackoff<T>(
    fn: () => Promise<T>,
    retries: number = this.MAX_RETRIES,
  ): Promise<T> {
    let lastError: Error | null = null;

    for (let attempt = 0; attempt < retries; attempt++) {
      try {
        return await fn();
      } catch (error) {
        lastError = error as Error;

        // Erreur métier (4xx) → throw immédiat, pas de retry.
        if (!this.isRetryable(error)) {
          throw lastError;
        }

        if (attempt < retries - 1) {
          const delay = this.RETRY_DELAY_MS * Math.pow(2, attempt);
          logger.warn(
            `Retry attempt ${attempt + 1}/${retries} after ${delay}ms`,
            "api",
            { error },
          );
          await new Promise((resolve) => setTimeout(resolve, delay));
        }
      }
    }

    throw lastError;
  }

  /**
   * Appelle Gemini API via Edge Function sécurisée
   * @param userInput Texte de l'utilisateur
   * @param prompt Prompt optionnel (si déjà formaté)
   * @returns Réponse Gemini ou erreur
   */
  async generateContent(
    userInput: string,
    prompt?: string,
    attachedFile?: GeminiAttachedFile,
    isChatOnly?: boolean,
    turnstileToken?: string,
    // aiFeature "analytics" : analyse IA d'un sondage, soumise côté serveur
    // au quota analytics (#1475) au lieu des conversations IA.
    options?: {
      maxRetries?: number;
      timeoutMs?: number;
      aiFeature?: "analytics";
    },
    // Édition d'une proposition existante (editPollFromText) : ne consomme
    // pas de conversation IA à vie, cf. doctrine "les modifications ne
    // comptent pas comme nouvelle conversation" (quotas.ts). Distinct de
    // isChatOnly (qui ne génère pas de sondage) — une édition génère bien
    // un sondage, mais pas un nouveau.
    isPollModification?: boolean,
  ): Promise<SecureGeminiResponse> {
    const perfStart = performance.now();
    const timings: Record<string, number> = {};

    try {
      if (!this.supabaseUrl) {
        return {
          success: false,
          error: "CONFIG_ERROR",
          message: "Configuration Supabase manquante",
        };
      }

      // OPTIMISATION: Récupérer la session avec un timeout très court (1s) ou skip si localStorage indique mode invité
      // Vérifier d'abord si on est probablement en mode invité pour éviter l'appel inutile
      let session = null;

      // Vérifier rapidement si une session existe dans localStorage (sans timeout long)
      // Supabase stocke la session dans localStorage avec une clé comme "sb-<project-ref>-auth-token"
      const hasStoredSession =
        typeof window !== "undefined" &&
        (() => {
          try {
            // Chercher toutes les clés Supabase dans localStorage
            for (let i = 0; i < localStorage.length; i++) {
              const key = localStorage.key(i);
              if (key && key.startsWith("sb-") && key.includes("-auth-token")) {
                const value = localStorage.getItem(key);
                if (value && value !== "null" && value !== "undefined") {
                  return true;
                }
              }
            }
            return false;
          } catch {
            return false;
          }
        })();

      if (hasStoredSession) {
        // Vérifier le cache mémoire d'abord
        const now = Date.now();
        if (this.cachedSession && this.cachedSession.expiresAt > now + 60000) {
          // Marge de 1 min
          session = { access_token: this.cachedSession.token };
          timings.client_session = 0.1; // Cache hit ultra rapide
        } else {
          try {
            const sessionStart = performance.now();
            session = await getSupabaseSessionWithTimeout(5000);
            timings.client_session = performance.now() - sessionStart;

            if (session?.access_token) {
              // Mettre en cache (on suppose 1h de validité par défaut si non spécifié)
              this.cachedSession = {
                token: session.access_token,
                expiresAt: now + 3600000,
              };
            }
          } catch {
            session = null;
          }
        }
      }

      // Utiliser le helper centralisé pour l'appel à l'Edge Function
      // Ce helper gère l'auth (apikey + Authorization), le parsing JSON et les erreurs HTTP
      // 2. Appel de l'Edge Function hyper-task via le helper centralisé
      // Pour les requêtes anonymes, récupérer le token Turnstile depuis le
      // store partagé (alimenté par <TurnstileWidget>). Si l'utilisateur est
      // authentifié (session présente), le token est ignoré côté serveur.
      const tokenFromStore = turnstileToken ?? getTurnstileToken() ?? undefined;

      // Pour les anonymes : envoyer le fingerprint navigateur (SHA-256, one-way)
      // afin que hyper-task puisse rate-limiter par fingerprint plutôt que par
      // IP seule. Les utilisateurs avec session n'en ont pas besoin.
      const guestFingerprint = !session
        ? await getCachedFingerprint().catch(() => undefined)
        : undefined;

      const result = await this.retryWithBackoff(async () => {
        return await invokeSupabaseFunction<string>(
          "hyper-task",
          {
            userInput,
            prompt: prompt,
            attachedFile,
            isChatOnly: isChatOnly,
            isPollModification: isPollModification,
            turnstileToken: tokenFromStore,
            guestFingerprint,
            // Clé envoyée seulement si définie : une edge function plus
            // ancienne l'ignore de toute façon.
            ...(options?.aiFeature ? { aiFeature: options.aiFeature } : {}),
            // Une analyse (aiFeature) a son propre quota : pas de scope.
            ...(this.conversationScope && !options?.aiFeature
              ? { conversationScope: this.conversationScope }
              : {}),
          },
          {
            timeout: options?.timeoutMs ?? 20000,
            requireAuth: false, // Guest access allowed; helper sends JWT when session exists
          },
        );
      }, options?.maxRetries);

      const data = result as {
        success?: boolean;
        data?: string;
        error?: string;
        message?: string;
        creditsRemaining?: number;
        timings?: {
          init: number;
          auth: number;
          rateLimit: number;
          quota: number;
          gemini: number;
          total: number;
        };
      } | null;
      const error = data?.error ? { message: data.error } : null; // Simulate error structure from old fetch

      if (data?.error) {
        logger.error("Edge Function error", "api", { message: data.error });

        // Refus du quota analytics (#1475) : code conservé tel quel pour
        // que l'appelant affiche le bon message. Testé AVANT QUOTA_EXCEEDED,
        // que « ANALYTICS_QUOTA_EXCEEDED » contient.
        const analyticsCode = analyticsErrorCode(data.error);
        if (analyticsCode) {
          return {
            success: false,
            error: analyticsCode,
            message: analyticsCode,
          };
        }

        // Gérer les erreurs spécifiques
        if (
          data.error.includes("QUOTA_EXCEEDED") ||
          data.error.includes("quota")
        ) {
          const creditsRemaining = data?.creditsRemaining ?? 0;
          const message =
            creditsRemaining === 0
              ? "Vos crédits IA sont épuisés. Vous pouvez consulter votre consommation dans le tableau de bord et attendre le prochain renouvellement, ou mettre à niveau votre compte pour obtenir plus de crédits."
              : `Il vous reste ${creditsRemaining} crédit${creditsRemaining > 1 ? "s" : ""} IA. Cette opération nécessite plus de crédits.`;

          return {
            success: false,
            error: "QUOTA_EXCEEDED",
            message,
            creditsRemaining,
          };
        }

        if (error && error.message?.includes("RATE_LIMIT")) {
          return {
            success: false,
            error: "RATE_LIMIT_EXCEEDED",
            message: "Trop de requêtes. Veuillez patienter avant de réessayer.",
          };
        }

        return {
          success: false,
          error: "API_ERROR",
          message:
            data.message || data.error || "Erreur lors de l'appel à l'API",
        };
      }

      if (!data || !data.success) {
        return {
          success: false,
          error: data?.error || "UNKNOWN_ERROR",
          message: data?.message || "Erreur lors de la génération",
          creditsRemaining: data?.creditsRemaining,
        };
      }

      const totalTime = performance.now() - perfStart;

      // Log de performance structuré
      const isDevEnv =
        typeof import.meta !== "undefined" && import.meta.env?.DEV;
      if (data.timings && isDevEnv) {
        console.log(`[SecureGeminiService] ⏱️ Performance Breakdown:
        - Total Client Roundtrip: ${totalTime.toFixed(2)}ms
        - Network/Edge Latency: ${(totalTime - data.timings.total).toFixed(2)}ms
        - Edge Processing: ${data.timings.total}ms
          - Gemini API: ${data.timings.gemini}ms
          - Quota Check: ${data.timings.quota}ms
          - Overhead: ${(data.timings.total - data.timings.gemini).toFixed(2)}ms
        `);
      }

      return {
        success: true,
        data: data.data,
        creditsRemaining: data.creditsRemaining,
        timings: data.timings,
      };
    } catch (error) {
      // invokeSupabaseFunction wrappe les erreurs HTTP non-OK en
      // ErrorFactory.network() (cf. supabaseApi.ts:469-482), ce qui efface
      // le code d'erreur retourné par l'Edge Function. On le reconstitue
      // ici en inspectant le message pour éviter de mapper un 403 QUOTA
      // sur "Erreur réseau" côté UI (bug #1 audit staging 2026-05-19).
      const message = error instanceof Error ? error.message : String(error);

      const analyticsCode = analyticsErrorCode(message);
      if (analyticsCode) {
        return { success: false, error: analyticsCode, message: analyticsCode };
      }

      if (message.includes("QUOTA_EXCEEDED") || message.includes("quota")) {
        return {
          success: false,
          error: "QUOTA_EXCEEDED",
          message:
            "Vos crédits IA sont épuisés. Vous pouvez consulter votre consommation dans le tableau de bord et attendre le prochain renouvellement, ou mettre à niveau votre compte pour obtenir plus de crédits.",
          creditsRemaining: 0,
        };
      }

      if (
        message.includes("TURNSTILE_REQUIRED") ||
        message.includes("TURNSTILE_INVALID")
      ) {
        return {
          success: false,
          error: "TURNSTILE_REQUIRED",
          message:
            "Vérification anti-robot requise. Rafraîchis la page et résous le captcha.",
        };
      }

      if (message.includes("RATE_LIMIT")) {
        return {
          success: false,
          error: "RATE_LIMIT_EXCEEDED",
          message: "Trop de requêtes. Veuillez patienter avant de réessayer.",
        };
      }

      const processedError = handleError(
        error,
        {
          component: "SecureGeminiService",
          operation: "generateContent",
        },
        "Erreur lors de l'appel sécurisé à Gemini",
      );

      logError(processedError, {
        component: "SecureGeminiService",
        operation: "generateContent",
      });

      return {
        success: false,
        error: "NETWORK_ERROR",
        message: "Erreur réseau lors de l'appel à l'API",
      };
    }
  }

  /**
   * Teste la connexion à l'Edge Function
   */
  async testConnection(): Promise<boolean> {
    try {
      // Single source of truth for auth + headers + URL: invokeSupabaseFunction.
      // Anything else duplicates the auth contract and risks drift (cf. the
      // hyper-task 401 regression that motivated this refactor).
      // requireAuth: false — les utilisateurs anonymes doivent aussi pouvoir
      // tester la connexion. L'Edge Function court-circuite testConnection
      // avant la vérif Turnstile (cf. eb711149), donc le ping passe sans
      // session ni token. Avant ce fix, un anon voyait "temporairement
      // indisponible" en boucle car invokeSupabaseFunction throw
      // "Authentication required" avant même d'atteindre l'Edge.
      await invokeSupabaseFunction(
        "hyper-task",
        { testConnection: true },
        { timeout: 5000, requireAuth: false },
      );
      return true;
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error);

      // Quota épuisé = la connexion fonctionne, c'est juste le quota.
      if (message.includes("QUOTA_EXCEEDED")) {
        logger.info(
          "✅ Edge Function accessible - Quota épuisé mais connexion OK",
        );
        return true;
      }

      // Turnstile requis = la connexion fonctionne, c'est juste le captcha.
      // Defense in depth : depuis 2026-05-18 l'Edge court-circuite testConnection
      // avant la vérif Turnstile, donc ce code ne devrait plus être atteint.
      // Conservé au cas où le déploiement Edge ne serait pas synchro.
      if (
        message.includes("TURNSTILE_REQUIRED") ||
        message.includes("TURNSTILE_INVALID")
      ) {
        logger.info(
          "✅ Edge Function accessible - Turnstile requis mais connexion OK",
        );
        return true;
      }

      logger.warn("🔍 Edge Function testConnection failed:", "api", message);
      logError(error instanceof Error ? error : new Error(message), {
        operation: "testConnection",
      });
      return false;
    }
  }
}

export const secureGeminiService = SecureGeminiService.getInstance();
